import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { type ReactNode, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import {
  formatAud,
  formatRange,
  lmiCostBullet,
  lmiCostHeadline,
  nextTierBullet,
  payNowVsSaveHeadline,
  refundBullets,
  refundHeadline,
  saveTo20Bullets,
  sourceBullet,
} from "@aucash/rules/lmi";

import { AppBar, Button, Card, Chip, Coin, Eyebrow } from "../../src/components/brand";
import { Checkbox, Field, MoneyInput, Segmented, YesNo } from "../../src/components/controls";
import { FULL_DISCLAIMER, SHORT_DISCLAIMER } from "../../src/disclaimer";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space, tabular } from "../../src/theme";

// lmi — Lenders Mortgage Insurance calculator, surfaced stateless-v1 (PLAN arch
// decision #1). One screen, three surfaces (indicative cost · pay-now-vs-save
// break-even · refund-on-discharge) over the LMI ruleset; the result renders
// INLINE (no save-by-id, no auth gate, no persistence). Each compute is a
// stateless `calc.lmi.compute` query — input -> output, no round-trip to a
// stored row. Copy comes verbatim from the rules layer's copy helpers so the
// wording can never drift from the API/SEO surfaces.
//
// REGULATORY POSTURE (rules module header, binding — NCCP/ACL):
//  - The premium is rendered as a clearly-labelled INDICATIVE RANGE
//    ("indicative, not a quote"), NEVER a single definite figure (estimate
//    -as-quote is the ACL misleading-conduct risk).
//  - The refund surface LEADS with the insurer disqualifier (Helia has
//    discontinued refunds) and never says "you're owed $X".
//  - There is NO broker/lender/loan-product handoff anywhere on this
//    personalised surface — the rules-layer `assertNoCreditHandoff` guard holds
//    at this UI because we render only its guarded output.

const ASSESSMENT_YEAR = "2025-26";

type Surface = "cost" | "break-even" | "refund";

const SURFACE_OPTIONS: ReadonlyArray<{ value: Surface; label: string }> = [
  { value: "cost", label: "LMI cost" },
  { value: "break-even", label: "Wait vs now" },
  { value: "refund", label: "Refund" },
];

const SURFACE_COPY: Record<
  Surface,
  { eyebrow: string; title: string; blurb: string }
> = {
  cost: {
    eyebrow: "FY26 · property",
    title: "What might LMI cost you?",
    blurb:
      "Lenders Mortgage Insurance is charged on home loans above 80% of the property value. See an indicative cost range from published lender rate guides — there is no single public LMI price list, so this is never a quote.",
  },
  "break-even": {
    eyebrow: "FY26 · deposit",
    title: "Buy now, or save to a 20% deposit?",
    blurb:
      "Buying now with a smaller deposit means paying LMI; waiting to 20% avoids it but the property may move. See how long saving to 20% could take under your own price-movement assumptions — a sensitivity band, not a forecast.",
  },
  refund: {
    eyebrow: "FY26 · discharge",
    title: "Could you get an LMI refund?",
    blurb:
      "Some insurers partly refund LMI if the loan is discharged early — but several have withdrawn refunds entirely, and the rules are strict. This starts with whether a refund is available at all.",
  },
};

type ComputeResult = Awaited<ReturnType<typeof trpc.calc.lmi.compute.query>>;

export default function LmiIndex(): JSX.Element {
  const router = useRouter();

  const [surface, setSurface] = useState<Surface>("cost");

  // cost + break-even shared property field
  const [propertyValue, setPropertyValue] = useState("");

  // cost
  const [deposit, setDeposit] = useState("");
  const [firstHomeGuaranteeEligible, setFirstHomeGuaranteeEligible] =
    useState(false);

  // break-even
  const [breakEvenDeposit, setBreakEvenDeposit] = useState("");
  const [annualSavingsRate, setAnnualSavingsRate] = useState("");

  // refund
  const [insurer, setInsurer] = useState<
    "helia" | "qbe" | "arch" | "unknown"
  >("unknown");
  const [premiumPaid, setPremiumPaid] = useState("");
  const [monthsSincePremium, setMonthsSincePremium] = useState("");
  const [fullyDischarged, setFullyDischarged] = useState(false);
  const [lenderNotifiedWithinWindow, setLenderNotifiedWithinWindow] =
    useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ComputeResult | null>(null);

  const num = (s: string): number => Number(s || "0");

  const onSurfaceChange = (next: Surface): void => {
    setSurface(next);
    setResult(null);
    setSubmitError(null);
    setErrors({});
  };

  const buildInput = (): {
    input: Parameters<typeof trpc.calc.lmi.compute.query>[0]["input"];
  } | null => {
    switch (surface) {
      case "cost":
        return {
          input: {
            surface: "cost",
            propertyValue: num(propertyValue),
            deposit: num(deposit),
            firstHomeGuaranteeEligible,
            assessmentYear: ASSESSMENT_YEAR,
          },
        };
      case "break-even":
        return {
          input: {
            surface: "break-even",
            propertyValue: num(propertyValue),
            deposit: num(breakEvenDeposit),
            annualSavingsRate: num(annualSavingsRate),
            assessmentYear: ASSESSMENT_YEAR,
          },
        };
      case "refund":
        return {
          input: {
            surface: "refund",
            insurer,
            premiumPaid: num(premiumPaid),
            monthsSincePremium: num(monthsSincePremium),
            fullyDischarged,
            lenderNotifiedWithinWindow,
            assessmentYear: ASSESSMENT_YEAR,
          },
        };
    }
  };

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    if (surface === "cost") {
      if (propertyValue.trim() === "")
        next.property = "Enter the property value.";
      if (deposit.trim() === "") next.deposit = "Enter your deposit.";
    }
    if (surface === "break-even") {
      if (propertyValue.trim() === "")
        next.property = "Enter the property value.";
      if (breakEvenDeposit.trim() === "")
        next.beDeposit = "Enter the deposit you have saved today.";
      if (annualSavingsRate.trim() === "")
        next.savings = "Enter how much you can add to the deposit each year.";
    }
    if (surface === "refund") {
      if (premiumPaid.trim() === "")
        next.premium = "Enter the LMI premium you paid.";
      if (monthsSincePremium.trim() === "")
        next.months = "Enter how many months ago the premium was paid.";
    }
    return next;
  };

  const onSubmit = (): void => {
    const next = validate();
    setErrors(next);
    const payload = buildInput();
    if (Object.keys(next).length > 0 || payload === null || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    setResult(null);
    trpc.calc.lmi.compute
      .query(payload)
      .then((res) => setResult(res))
      .catch((err: unknown) =>
        setSubmitError(
          err instanceof Error ? err.message : "We couldn't run the calculator.",
        ),
      )
      .finally(() => setSubmitting(false));
  };

  const copy = SURFACE_COPY[surface];

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="LMI" />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <View style={styles.navRow}>
            <Pressable onPress={() => router.push("/")} accessibilityRole="button">
              <Text style={styles.navLink}>← Calculators</Text>
            </Pressable>
          </View>

          <View style={styles.header}>
            <Coin size={56} />
            <Eyebrow>{copy.eyebrow}</Eyebrow>
            <Text style={styles.title}>{copy.title}</Text>
            <Text style={styles.subtitle}>{copy.blurb}</Text>
            <Text style={styles.shortDisclaimer}>{SHORT_DISCLAIMER}</Text>
          </View>

          <Field label="Which question are you answering?">
            <Segmented<Surface>
              options={SURFACE_OPTIONS}
              value={surface}
              onChange={onSurfaceChange}
              testIDPrefix="surface"
            />
          </Field>

          {surface === "cost" ? (
            <>
              <Field
                label="Property value"
                help="The purchase price or valuation."
                error={errors.property}
              >
                <MoneyInput
                  value={propertyValue}
                  onChange={setPropertyValue}
                  testID="input-property-value"
                  accessibilityLabel="Property value"
                />
              </Field>
              <Field
                label="Your deposit"
                help="LMI is generally charged on loans above 80% of the property value."
                error={errors.deposit}
              >
                <MoneyInput
                  value={deposit}
                  onChange={setDeposit}
                  testID="input-deposit"
                  accessibilityLabel="Your deposit"
                />
              </Field>
              <Field label="Eligible for the First Home Guarantee (5% Deposit Scheme)?">
                <Checkbox
                  checked={firstHomeGuaranteeEligible}
                  onChange={setFirstHomeGuaranteeEligible}
                  label="Yes — the Commonwealth guarantees the deposit gap, so the lender requires no LMI."
                />
              </Field>
            </>
          ) : null}

          {surface === "break-even" ? (
            <>
              <Field
                label="Property value today"
                error={errors.property}
              >
                <MoneyInput
                  value={propertyValue}
                  onChange={setPropertyValue}
                  testID="input-be-property-value"
                  accessibilityLabel="Property value today"
                />
              </Field>
              <Field
                label="Deposit saved today"
                error={errors.beDeposit}
              >
                <MoneyInput
                  value={breakEvenDeposit}
                  onChange={setBreakEvenDeposit}
                  testID="input-be-deposit"
                  accessibilityLabel="Deposit saved today"
                />
              </Field>
              <Field
                label="How much you can add to the deposit each year"
                help="We model how long saving to a 20% deposit could take under different price-movement assumptions you choose."
                error={errors.savings}
              >
                <MoneyInput
                  value={annualSavingsRate}
                  onChange={setAnnualSavingsRate}
                  testID="input-annual-savings"
                  accessibilityLabel="Annual savings toward the deposit"
                />
              </Field>
            </>
          ) : null}

          {surface === "refund" ? (
            <>
              <Field
                label="Who is your LMI insurer?"
                help="Refund policy varies by insurer, and several have withdrawn refunds. If you're not sure, leave it as 'Not sure'."
              >
                <Segmented<"helia" | "qbe" | "arch" | "unknown">
                  options={[
                    { value: "helia", label: "Helia" },
                    { value: "qbe", label: "QBE" },
                    { value: "arch", label: "Arch" },
                    { value: "unknown", label: "Not sure" },
                  ]}
                  value={insurer}
                  onChange={setInsurer}
                  testIDPrefix="insurer"
                />
              </Field>
              <Field
                label="LMI premium you paid"
                error={errors.premium}
              >
                <MoneyInput
                  value={premiumPaid}
                  onChange={setPremiumPaid}
                  testID="input-premium-paid"
                  accessibilityLabel="LMI premium you paid"
                />
              </Field>
              <Field
                label="Months since you paid the premium"
                help="Refunds typically apply only within about 24 months of the premium being paid."
                error={errors.months}
              >
                <MoneyInput
                  value={monthsSincePremium}
                  onChange={setMonthsSincePremium}
                  testID="input-months-since"
                  accessibilityLabel="Months since you paid the premium"
                />
              </Field>
              <Field label="Is the mortgage being fully discharged (all loans on the property repaid)?">
                <YesNo
                  value={fullyDischarged}
                  onChange={setFullyDischarged}
                  yesLabel="Yes — fully discharged"
                  noLabel="No / partial discharge"
                  testIDPrefix="discharged"
                />
              </Field>
              <Field label="Can the lender notify the insurer within about 30 days of discharge?">
                <YesNo
                  value={lenderNotifiedWithinWindow}
                  onChange={setLenderNotifiedWithinWindow}
                  yesLabel="Yes — within the window"
                  noLabel="No / unsure"
                  testIDPrefix="notified"
                />
              </Field>
            </>
          ) : null}

          <Button
            label={submitting ? "Calculating…" : "See your estimate →"}
            onPress={onSubmit}
            disabled={submitting}
            full
            testID="lmi-submit"
          />
          {submitError !== null ? (
            <Text style={styles.submitError}>{submitError}</Text>
          ) : null}

          {submitting && result === null ? (
            <View style={styles.loading}>
              <ActivityIndicator color={colors.honey} />
            </View>
          ) : null}

          {result !== null ? <ResultBlock result={result} /> : null}
        </View>
      </ScrollView>
    </View>
  );
}

/** Inline result — stateless, rendered straight under the form (no save-by-id).
 * The full disclaimer (rules-layer DISCLAIMER) sits at the TOP, before any
 * number (validation §3.2 placement rule). The premium is rendered as a clearly
 * -labelled INDICATIVE RANGE, and the refund surface LEADS with disqualifiers
 * (never "you're owed $X"). */
function ResultBlock({ result }: { result: ComputeResult }): JSX.Element {
  return (
    <View style={styles.resultStage}>
      {/* §3.2: full disclaimer at the top, before any number. */}
      <View style={styles.disclaimerBlock}>
        <Text style={styles.disclaimerText}>{FULL_DISCLAIMER}</Text>
      </View>

      <View style={styles.resHead}>
        <Eyebrow>Indicative · FY26</Eyebrow>
        <Chip label="Indicative — not a quote" tone="est" />
      </View>

      <SurfaceResult result={result} />

      <Section title="Where these numbers come from">
        <Text style={styles.bodyText}>
          {sourceBullet(result.ruleVersion, result.sourcesVerifiedAt)}
        </Text>
        {Object.entries(result.sources).map(([key, url]) => (
          <SourceLink key={key} label={url} url={url} />
        ))}
        <Text style={styles.verified}>
          Rules last verified {result.sourcesVerifiedAt} · ruleset{" "}
          {result.ruleVersion}
        </Text>
      </Section>
    </View>
  );
}

/** Dispatch to the surface's rules-layer copy. All wording comes from the rules
 * package — no inline figures, no advice framing. */
function SurfaceResult({ result }: { result: ComputeResult }): JSX.Element {
  const out = result.output;

  if (result.surface === "cost") {
    const o = out as Extract<typeof out, { isIndicativeEstimate: true }>;
    const tier = nextTierBullet(o);
    return (
      <>
        <Card style={{ gap: space.sm }}>
          <Text style={styles.lead}>{lmiCostHeadline(o)}</Text>
          {/* The premium itself, rendered explicitly as an indicative RANGE. */}
          {o.lmiApplies ? (
            <Text style={styles.rangeLine}>
              Indicative range: {formatRange(o.indicativePremium)}
            </Text>
          ) : null}
          <Text style={styles.bodyText}>{lmiCostBullet(o)}</Text>
          {tier !== null ? <Text style={styles.bodyText}>{tier}</Text> : null}
        </Card>
      </>
    );
  }

  if (result.surface === "break-even") {
    const o = out as Extract<typeof out, { isSensitivityBand: true }>;
    return (
      <Card style={{ gap: space.sm }}>
        <Text style={styles.lead}>{payNowVsSaveHeadline(o)}</Text>
        {saveTo20Bullets(o).map((b, i) => (
          <Text key={i} style={styles.bodyText}>
            {b}
          </Text>
        ))}
      </Card>
    );
  }

  // refund — LEADS with disqualifiers; no dollar figure unless eligible.
  const o = out as Extract<typeof out, { isIndicativePossibleRefund: true }>;
  return (
    <Card style={{ gap: space.sm }}>
      <Text style={styles.lead}>{refundHeadline(o)}</Text>
      {refundBullets(o).map((b, i) => (
        <Text key={i} style={styles.bodyText}>
          {b}
        </Text>
      ))}
    </Card>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function SourceLink({
  label,
  url,
}: {
  label: string;
  url: string | undefined;
}): JSX.Element | null {
  if (url === undefined) return null;
  return (
    <Pressable
      onPress={() => void Linking.openURL(url)}
      accessibilityRole="link"
      style={styles.sourceLink}
    >
      <Text style={styles.sourceLinkText}>{label}</Text>
    </Pressable>
  );
}

// `formatAud` is imported for parity with the other screens' result helpers; the
// headline strings already embed the formatted figures, so it isn't called
// directly here. Referenced to keep the import meaningful without a no-op.
void formatAud;

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 560, gap: space.xl },
  navRow: { flexDirection: "row", justifyContent: "space-between" },
  navLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  header: { gap: space.sm },
  title: {
    fontFamily: fonts.display,
    fontSize: 30,
    lineHeight: 34,
    letterSpacing: -1,
    color: colors.ink,
    marginTop: space.xs,
  },
  subtitle: { fontFamily: fonts.body, fontSize: 16, lineHeight: 23, color: colors.inkSoft },
  shortDisclaimer: {
    fontFamily: fonts.bodySemibold,
    fontSize: 12,
    color: colors.muted,
    marginTop: space.xs,
  },
  submitError: {
    fontFamily: fonts.bodyMedium,
    fontSize: 14,
    color: colors.danger,
    textAlign: "center",
  },
  loading: { paddingVertical: space.xl, alignItems: "center" },

  resultStage: { gap: space.xl, marginTop: space.sm },
  disclaimerBlock: {
    backgroundColor: colors.sand,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.lg,
  },
  disclaimerText: {
    fontFamily: fonts.body,
    fontSize: 12,
    lineHeight: 18,
    color: colors.inkSoft,
  },
  resHead: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  lead: {
    fontFamily: fonts.displayHeavy,
    fontSize: 24,
    lineHeight: 30,
    letterSpacing: -0.5,
    color: colors.ink,
    ...tabular,
  },
  rangeLine: {
    fontFamily: fonts.bodySemibold,
    fontSize: 18,
    color: colors.honeyDeep,
    ...tabular,
  },
  bodyText: { fontFamily: fonts.body, fontSize: 14.5, lineHeight: 22, color: colors.inkSoft },

  section: { gap: space.md },
  sectionTitle: {
    fontFamily: fonts.display,
    fontSize: 20,
    letterSpacing: -0.4,
    color: colors.ink,
  },
  sourceLink: {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.md,
    backgroundColor: colors.card,
  },
  sourceLinkText: { fontFamily: fonts.bodySemibold, fontSize: 13, color: colors.oceanDeep },
  verified: { fontFamily: fonts.body, fontSize: 12, color: colors.muted },
});
